import React from 'react';

export interface CompanyPageType {
  name: string;
  bgImage?: string;
  profileImage?: string;
  imageStyle?: React.CSSProperties;
  overLayStyle?: React.CSSProperties;
  logo?: string;
  svg?: React.ReactNode; // or JSX.Element
  title?: string;
  description?: string;
  category?: string;
  website?: string;
  profileTitle?: string;
  profileSummary?: string;
  profilePoints?: string[];
  accentColor?: string;
  titleColor?: string;
  descColor?: string;
  phone?: string;
  email?: string;
  hours?: string;
  floor?: string;
}

const getWebsiteLabel = (website?: string) => {
  if (!website) return undefined;

  try {
    return new URL(website).hostname.replace(/^www\./, '');
  } catch {
    return website;
  }
};

export const CompanyPage: React.FC<CompanyPageType> = ({
  name,
  bgImage,
  profileImage,
  title,
  logo,
  svg,
  description,
  category,
  website,
  profileTitle,
  profileSummary,
  profilePoints,
  accentColor,
  titleColor,
  phone,
  email,
  hours,
  floor,
}) => {
  const displayTitle = profileTitle ?? title ?? name;
  const displaySummary = profileSummary ?? description;
  const accent = accentColor ?? titleColor ?? '#0f8b8d';
  const backgroundImage = profileImage ?? bgImage;
  const websiteLabel = getWebsiteLabel(website);
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('');

  return (
    <section
      className="company-page"
      style={
        {
          '--company-accent': accent,
          backgroundImage: backgroundImage
            ? `linear-gradient(115deg, rgba(6, 20, 23, 0.88) 0%, rgba(6, 20, 23, 0.74) 42%, rgba(6, 20, 23, 0.38) 100%), url(${backgroundImage})`
            : undefined,
        } as React.CSSProperties
      }
    >
      <div className="company-page__texture" />
      <div className="company-page__content">
        <div className="company-page__eyebrow">
          {floor && <span>{floor}</span>}
          {category && <span>{category}</span>}
        </div>

        <div className="company-page__brand">
          <div className="company-page__logo-wrap">
            {svg ? (
              <div className="company-page__svg-logo">{svg}</div>
            ) : logo ? (
              <img src={logo} alt={`${name} logo`} />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <div>
            <p className="company-page__label">Leietakerprofil</p>
            <h1>{name}</h1>
          </div>
        </div>

        <div className="company-page__copy">
          <h2>{displayTitle}</h2>
          {displaySummary && <p>{displaySummary}</p>}
        </div>

        {profilePoints && profilePoints.length > 0 && (
          <div className="company-page__points">
            {profilePoints.map((point) => (
              <div key={point} className="company-page__point">
                <span />
                {point}
              </div>
            ))}
          </div>
        )}

        <div className="company-page__details">
          {websiteLabel && <span>{websiteLabel}</span>}
          {phone && <span>{phone}</span>}
          {email && <span>{email}</span>}
          {hours && <span>{hours}</span>}
        </div>
      </div>
    </section>
  );
};
